// canvasSettings.js
//
// Canvas appearance settings: background palette, node shading, the default
// connector style, and canvas font sizes. The whole Canvas appearance dialog
// is a USER preference: settings are stored per user (sam-dbam user-prefs,
// GCS-backed) and apply to every survey that user opens. They are no longer
// written to the survey payload (see docs/designer_layout_and_appearance_spec.md).

// Pale background palette. Colors sit in the same families as the
// design tokens (UI artifacts/design-tokens.css) but lightened so the
// dot grid and node borders stay readable.
export const CANVAS_BACKGROUNDS = [
  { id: 'white', label: 'White', color: '#FFFFFF' },
  { id: 'mist', label: 'Mist', color: 'var(--color-bg-base)' },
  { id: 'blue', label: 'Blue', color: '#EFF5FA' },
  { id: 'aqua', label: 'Aqua', color: '#EDF6F6' },
  { id: 'green', label: 'Green', color: '#EFF6F0' },
  { id: 'sand', label: 'Sand', color: '#F8F5EE' },
  { id: 'lavender', label: 'Lavender', color: '#F4F1F9' },
  { id: 'blush', label: 'Blush', color: '#FAF1F0' },
  { id: 'gray', label: 'Gray', color: '#F1F1F1' },
];

export const CONNECTOR_STYLES = [
  { id: 'smoothstep', label: 'Smooth Step' },
  { id: 'default', label: 'Curved' },
  { id: 'step', label: 'Step' },
  { id: 'straight', label: 'Straight' },
];

// Font sizes are chosen in points (1 pt = 4/3 px).
export const FONT_SIZE_POINTS = [7, 8, 9, 10, 10.5, 11, 12, 14, 16, 18];

export const ptToPx = (pt) => Math.round((pt * 4) / 3);

// Directional arrowhead size on connectors, in absolute px (userSpaceOnUse).
// 0 hides the arrowheads entirely.
export const ARROW_SIZE_OPTIONS = [
  { value: 0, label: 'None' },
  { value: 14, label: 'Small' },
  { value: 18, label: 'Medium' },
  { value: 24, label: 'Large' },
];

export const DEFAULT_CANVAS_SETTINGS = {
  background: '#FFFFFF',
  shadeByType: false,
  defaultEdgeType: 'smoothstep',
  nodeTitlePt: 9,    // node title / ID line (12 px)
  nodeTextPt: 10,    // node body text (~13 px)
  edgeTextPt: 10.5,  // edge labels (14 px — raised from the old 12 px)
  arrowSize: 18,     // connector arrowhead size (Medium)
};

/**
 * Merge a raw saved settings object (possibly missing, partial, or from an
 * older file) with defaults, discarding anything unrecognized.
 */
export function normalizeCanvasSettings(raw) {
  const out = { ...DEFAULT_CANVAS_SETTINGS };
  if (raw && typeof raw === 'object') {
    if (typeof raw.background === 'string' && /^#[0-9A-Fa-f]{6}$/.test(raw.background)) {
      out.background = raw.background;
    }
    if (typeof raw.shadeByType === 'boolean') out.shadeByType = raw.shadeByType;
    if (CONNECTOR_STYLES.some(s => s.id === raw.defaultEdgeType)) {
      out.defaultEdgeType = raw.defaultEdgeType;
    }
    for (const key of ['nodeTitlePt', 'nodeTextPt', 'edgeTextPt']) {
      if (FONT_SIZE_POINTS.includes(raw[key])) out[key] = raw[key];
    }
    if (ARROW_SIZE_OPTIONS.some(o => o.value === raw.arrowSize)) {
      out.arrowSize = raw.arrowSize;
    }
  }
  return out;
}
